import { add, filter, get, multiply, set, sortBy } from 'lodash';
import { FORM, POINT_TYPE, REGEX, SHIPMENT } from '../constants';
import { ObjectUtil } from '../utils';
import { isNiShipment, validateParcelDimensionsForShop } from './shipment';
import { isCountryDomestic } from './address';

export const getNetworkQuery = values => {
  const data = ObjectUtil.pickDeep(values, [
    FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY,
    FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY,
    FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY,
    FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
    FORM.SHIPMENT_FIELDS.COLLECTION_COUNTRY.KEY,
    FORM.SHIPMENT_FIELDS.COLLECTION_CITY.KEY,
    FORM.SHIPMENT_FIELDS.COLLECTION_COUNTY.KEY,
    FORM.SHIPMENT_FIELDS.COLLECTION_POSTCODE.KEY,

    FORM.SHIPMENT_FIELDS.TOTAL_WEIGHT.KEY,
    FORM.SHIPMENT_FIELDS.NUMBER_OF_PARCELS.KEY,
  ]);

  if (!get(data, FORM.SHIPMENT_FIELDS.TOTAL_WEIGHT.KEY)) {
    set(data, FORM.SHIPMENT_FIELDS.TOTAL_WEIGHT.KEY, 1);
  }

  if (!get(data, FORM.SHIPMENT_FIELDS.NUMBER_OF_PARCELS.KEY)) {
    set(data, FORM.SHIPMENT_FIELDS.NUMBER_OF_PARCELS.KEY, 1);
  }

  const query = data.outboundConsignment;

  const isNiZone = isNiShipment(values);

  if (isNiZone) {
    const company = get(
      values,
      FORM.SHIPMENT_FIELDS.COLLECTION_ORGANISATION.KEY
    );
    query.isBusiness = get(
      values,
      FORM.SHIPMENT_FIELDS.INVOICE_IMPORTER_IS_BUSINESS.KEY,
      !!company
    );
    query.atRisk = get(
      values,
      FORM.SHIPMENT_FIELDS.INVOICE_IMPORTER_AT_RISK.KEY,
      false
    );
  }

  return query;
};

export const getNiRequiredFieldsQuery = values => ({
  countryCode: get(values, FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY, ''),
  postcode: get(values, FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY, ''),
  isBusiness: get(
    values,
    FORM.SHIPMENT_FIELDS.INVOICE_IMPORTER_IS_BUSINESS.KEY,
    false
  ),
  atRisk: get(values, FORM.SHIPMENT_FIELDS.INVOICE_IMPORTER_AT_RISK.KEY, false),
});

export const getDriverSurchargePrice = ({ network }) =>
  network ? get(network, 'price.driverCollectionSurcharge', 0) : 0;

export const getDriverSurcharge = ({ shipment, network }) => {
  let driverSurchargePrice = 0;
  const isCollectedFromDoor =
    get(shipment, FORM.SHIPMENT_FIELDS.COLLECT_FROM.KEY) === POINT_TYPE.DOOR;
  if (isCollectedFromDoor) {
    driverSurchargePrice = getDriverSurchargePrice({ network });
  }
  return driverSurchargePrice;
};

export const getTotalPrice = ({ shipment, network }) => {
  const parcels = get(shipment, FORM.SHIPMENT_FIELDS.PARCELS.KEY, []);
  let driverSurcharge = 0;
  let taxAmount = 0;
  let totalIncVatAmount = 0;

  if (parcels.length > 0) {
    let totalExcVatAmount = getBasePriceForNetwork({ parcels, network });
    driverSurcharge = getDriverSurcharge({ shipment, network });
    totalExcVatAmount = totalExcVatAmount + driverSurcharge;
    const vatRate = get(network, 'price.vatRate', 0);
    taxAmount =
      Math.round(((totalExcVatAmount * vatRate) / 100) * 100) / 100 || 0;
    totalIncVatAmount = totalExcVatAmount + taxAmount || 0;
  }

  return {
    taxAmount,
    totalIncVatAmount,
    driverSurcharge,
  };
};

export const getBasePriceForNetwork = ({ parcels, network }) => {
  if (!network) {
    return;
  }

  return parcels?.reduce((total, parcel) => {
    const parcelPrice = getParcelPrice({
      parcel,
      network,
    });
    return total + parcelPrice * (parcel._quantity || 1);
  }, 0);
};

export const getParcelPrice = ({ parcel, network }) => {
  if (!network) {
    return;
  }
  const basePrice = network.price?.baseRate;

  const extraWeightPrice = getExtraWeightPriceForParcel({
    parcel,
    network,
  });

  return basePrice + extraWeightPrice;
};

export const calculateExtraWeightPrice = ({
  weight,
  baseRateWeight,
  additionalRateWeight,
  additionalRate,
}) => {
  // Calculate extra weight
  const extraWeight = weight > baseRateWeight ? weight - baseRateWeight : 0;

  // Calculate additional charge based on increments
  const additionalCharges =
    additionalRateWeight !== 0
      ? Math.ceil(extraWeight / additionalRateWeight) * (additionalRate || 1)
      : 0;

  return additionalCharges || 0;
};

export const getExtraWeightPriceForParcel = ({ parcel, network }) => {
  if (!network) {
    return;
  }
  const weight = parcel._weight;
  const baseRateWeight = get(network, 'price.baseRateWeight');

  const additionalRateWeight = get(network, 'price.additionalRateWeight', 0);
  const additionalRate = get(network, 'price.additionalRate', 0);

  return calculateExtraWeightPrice({
    weight,
    baseRateWeight,
    additionalRateWeight,
    additionalRate,
  });
};

export const getDefaultNetwork = ({ shipment, minNet }) => {
  if (!minNet) {
    return;
  }
  const isDroppedToDoor =
    get(shipment, FORM.SHIPMENT_FIELDS.DELIVER_TO.KEY) === POINT_TYPE.DOOR;
  return isDroppedToDoor
    ? minNet.minFromDoorPriceNetwork
    : minNet.minFromShopPriceNetwork;
};

export const getMinPriceNetworks = (networks = [], parcels = []) => {
  const minFromShopPriceNetwork = sortBy(
    networks.filter(
      network => network.price.baseRate && network.networkKey === '2^91'
    ),
    'price.baseRate'
  )[0];

  const minFromDoorPriceNetwork = sortBy(
    networks.filter(
      network => network.price.baseRate && network.networkKey !== '2^91'
    ),
    network =>
      getBasePriceForNetwork({
        network,
        parcels,
      })
  )[0];

  return { minFromShopPriceNetwork, minFromDoorPriceNetwork };
};
/**
 *
 * @param {Array} networks - Array of networks
 * @param {number} shipment - Shipment
 * @returns {Array} - Available networks based on number of parcels and their weights
 */
export const getNetworksByParcelSizeAndWeight = (networks = [], shipment) =>
  networks.filter(({ networkDetails }) => {
    const deliveryCountry = get(
      shipment,
      FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY
    );
    const parcels = get(shipment, FORM.SHIPMENT_FIELDS.PARCELS.KEY, []);

    return (
      parcels.every(({ _weight = 0, _height = 0, _width = 0, _length = 0 }) => {
        const weight = Number(_weight) || 0;
        const height = Number(_height) || 0;
        const width = Number(_width) || 0;
        const length = Number(_length) || 0;
        const parcelGirth = isCountryDomestic(deliveryCountry)
          ? Number(height) + Number(width) + Number(length)
          : Number(length) + 2 * Number(height) + 2 * Number(width);

        return (
          weight <= networkDetails?.maxWeight / 1000 &&
          Math.max(height, width, length) <= networkDetails?.maxLength &&
          parcelGirth <= networkDetails?.maxGirth
        );
      }) &&
      (get(shipment, FORM.SHIPMENT_FIELDS.NUMBER_OF_PARCELS.KEY) || 0) <=
        networkDetails?.maxParcels
    );
  });

export const sortNetworksByPrice = (networks, parcels) =>
  networks.sort((prev, curr) => {
    const prevBasePrice = getBasePriceForNetwork({
      parcels,
      network: prev,
    });
    const currBasePrice = getBasePriceForNetwork({
      parcels,
      network: curr,
    });

    return (prevBasePrice || Infinity) - (currBasePrice || Infinity);
  });

export const getParcelsTotalWeight = (shipment, customParcels = []) => {
  const parcels = customParcels.length
    ? customParcels
    : get(shipment, FORM.SHIPMENT_FIELDS.PARCELS.KEY) || [];

  return parcels.reduce(
    (acc, curr) =>
      add(
        acc,
        multiply(
          Number(curr._weight || 0),
          Number.parseInt(curr._quantity || 0)
        )
      ),
    0
  );
};

export const getParcelTotalQuantity = (parcels = []) =>
  parcels.reduce(
    (acc, curr) => add(acc, Number.parseInt(curr._quantity || 0)),
    0
  );

export const getParcelsTotalValue = (parcels = []) =>
  parcels.reduce(
    (acc, curr) =>
      add(
        acc,
        multiply(Number(curr._value || 0), Number.parseInt(curr._quantity || 0))
      ),
    0
  );

/**
 *
 * @param {Array} networks - List of networks
 * @returns Maximum weight and parcel number for networks
 */
export const getMaxParcelDimensions = (networks = []) => {
  const { maxWeight, maxParcelNumber, maxLength } = networks.reduce(
    (acc, curr) => {
      let maxWeight = acc.maxWeight;
      let maxParcelNumber = acc.maxParcelNumber;
      let maxLength = acc.maxLength;

      if (curr.networkDetails?.maxWeight) {
        maxWeight = Math.max(maxWeight, curr.networkDetails?.maxWeight / 1000);
      }
      if (curr.networkDetails?.maxParcels) {
        maxParcelNumber = Math.max(
          maxParcelNumber,
          curr.networkDetails?.maxParcels
        );
      }
      if (curr.networkDetails?.maxLength) {
        maxLength = Math.max(maxLength, curr.networkDetails?.maxLength);
      }

      return {
        maxWeight,
        maxParcelNumber,
        maxLength,
      };
    },
    {
      maxWeight: 0,
      maxParcelNumber: 0,
      maxLength: 0,
    }
  );

  return {
    maxWeight: maxWeight || 30,
    maxParcelNumber: maxParcelNumber || 99,
    maxLength: maxLength || 200,
  };
};

const getArrivalServicesExtraPrices = ({
  networks,
  shipment,
  minDoorPrice,
  minFromDoorPriceNetwork,
}) => {
  const arrivalServicesNetworks = filter(networks, network => {
    const baseRate = getBasePriceForNetwork({
      network,
      parcels: get(shipment, FORM.SHIPMENT_FIELDS.PARCELS.KEY),
    });

    return baseRate && baseRate >= minDoorPrice;
  });

  return arrivalServicesNetworks.map(network => {
    const servicePrice = getBasePriceForNetwork({
      network,
      parcels: get(shipment, FORM.SHIPMENT_FIELDS.PARCELS.KEY),
    });

    return {
      networkKey: network?.networkKey,
      extraPrice:
        servicePrice -
        minDoorPrice +
        ((network?.price?.driverCollectionSurcharge || 0) -
          (minFromDoorPriceNetwork?.price?.driverCollectionSurcharge || 0)),
    };
  });
};

export const getPriceData = ({ price = {}, networks, shipment }) => {
  const parcels = get(shipment, FORM.SHIPMENT_FIELDS.PARCELS.KEY, []);
  const selectedNetwork = get(shipment, FORM.SHIPMENT_FIELDS.NETWORK.KEY);
  const minNet = getMinPriceNetworks(networks, parcels);
  const defaultNetwork = getDefaultNetwork({ shipment, minNet });
  const defaultDriverSurchargePrice = getDriverSurchargePrice({
    // network: selectedNetwork || defaultNetwork,
    network: defaultNetwork,
  });

  const totalPrice = getTotalPrice({
    network: selectedNetwork || defaultNetwork,
    shipment,
  });
  const minDoorPrice = getBasePriceForNetwork({
    parcels,
    network: minNet.minFromDoorPriceNetwork,
  });

  return {
    ...price,
    basePrice: getBasePriceForNetwork({ parcels, network: defaultNetwork }),
    minShopPrice: getBasePriceForNetwork({
      parcels,
      network: minNet.minFromShopPriceNetwork,
    }),
    minDoorPrice,
    deliveryExtraPrice: getBasePriceForNetwork({
      parcels,
      network: minNet.minFromDoorPriceNetwork,
    }),
    collectionExtraPrice: defaultDriverSurchargePrice || 0,
    taxAmount: price.taxAmount || totalPrice.taxAmount || 0,
    totalIncVatAmount:
      price.totalIncVatAmount || totalPrice.totalIncVatAmount || 0,
    arrivalServicesExtraPrices: getArrivalServicesExtraPrices({
      networks,
      shipment,
      minDoorPrice,
      minFromDoorPriceNetwork: minNet.minFromDoorPriceNetwork,
    }),
  };
};

export const removeBusinessUnit = value =>
  typeof value === 'string' ? value.replace(/^[12]{1}\^/, '') : '';

export const getNetworkByCode = (networks = [], code = '') =>
  networks.find(({ networkKey }) => networkKey === code);

export const getConfigByNetwork = network => {
  if (!network) {
    return {};
  }

  const shipToPickup = get(network, 'networkDetails.shipToPickup', 'N');
  const premium = get(network, 'networkDetails.premium', 'N');

  if (premium === 'Y') {
    return {
      collectFrom: POINT_TYPE.DOOR,
      deliverTo: POINT_TYPE.DOOR,
    };
  }

  const config = {
    deliverTo: shipToPickup === 'Y' ? POINT_TYPE.SHOP : POINT_TYPE.DOOR,
    collectFrom: POINT_TYPE.SHOP,
  };

  return config;
};

export const getShipToShopAvailable = (networks, values) => {
  const parcels = get(values, FORM.SHIPMENT_FIELDS.PARCELS.KEY, []);
  const dimensionsSuitable = validateParcelDimensionsForShop(parcels);

  return (
    networks.some(
      ({ networkDetails }) =>
        networkDetails.shipToPickup === SHIPMENT.REQUIRED_TYPE.OPTIONAL
    ) && dimensionsSuitable
  );
};

export const extractTimeFromServiceDesc = (
  serviceDesc,
  defaultTime,
  hoursOnly = false
) => {
  if (typeof serviceDesc !== 'string') {
    return defaultTime;
  }

  const timeMatch = serviceDesc.match(REGEX.TIME_REGEX);

  if (!timeMatch) {
    return defaultTime;
  }

  return hoursOnly ? timeMatch[1] : timeMatch[0];
};

export const getBasketServiceName = (network = {}) => {
  const { networkKey, service } = network;
  const serviceDesc = service?.serviceDesc;

  switch (networkKey) {
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_10_30:
      return `Parcel By ${extractTimeFromServiceDesc(serviceDesc, '10:30')}`;
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_12:
      return `Parcel By ${extractTimeFromServiceDesc(serviceDesc, '12', true)}`;
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_SAT_12:
      return `Parcel Sat By ${extractTimeFromServiceDesc(
        serviceDesc,
        '12',
        true
      )}`;
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_SUN_12:
      return `Parcel Sun By ${extractTimeFromServiceDesc(
        serviceDesc,
        '12',
        true
      )}`;
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_NEXT_DAY:
      return 'Parcel Next day';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_SHIP_TO_SHOP:
      return 'Parcel Ship to Shop';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_TWO_DAY:
      return 'Two day Parcel';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_SAT:
      return 'Parcel Saturday';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_SUN:
      return 'Parcel Sunday';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_EUROPE_ROAD:
      return 'Parcel - DPD Europe By Road';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_AIR_CLASSIC:
      return 'Air Classic';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_AIR_EXPRESS:
      return 'Air Express';
    case SHIPMENT.NETWORK_KEYS.NETWORK_KEY_DIRECT_LITE:
      return 'DPD Direct Lite';
    default:
      return serviceDesc;
  }
};
